import { TooltipPointerConfig, TooltipPointerPosition, TooltipPosition, TooltipPositionConfig } from '../types/charts';
import { TOOLTIP_POSITION } from '../ui/Dashboard/EnergyFlow/constants';

/**
 * Calculates the constrained position for a tooltip flyout within chart boundaries.
 *
 * The function ensures the flyout stays within the visible chart area by:
 * 1. Calculating the initial position based on orientation (above or below the data point)
 * 2. Constraining horizontal position to stay within left/right margins
 * 3. Constraining vertical position to stay within top/bottom margins
 *
 * @param config - Configuration object containing position parameters
 * @returns The calculated flyout position constrained to visible area
 *
 * @example
 * const position = calculateTooltipFlyoutPosition({
 *   x: 200,
 *   y: 150,
 *   orientation: 'bottom',
 *   flyoutWidth: 200,
 *   flyoutHeight: 100,
 *   chartWidth: 648,
 *   chartHeight: 400,
 *   margin: 10,
 * });
 * // Returns: { flyoutX: 100, flyoutY: 40 }
 */
export const calculateTooltipFlyoutPosition = (config: TooltipPositionConfig): TooltipPosition => {
  const {
    x,
    y,
    orientation,
    flyoutWidth,
    flyoutHeight,
    chartWidth,
    chartHeight,
    margin = TOOLTIP_POSITION.CHART_MARGIN,
  } = config;

  // Calculate initial flyout position based on orientation
  // 'bottom' orientation means tooltip appears above the point (y - height - gap)
  // 'top' orientation means tooltip appears below the point (y + gap)
  let flyoutY =
    orientation === 'bottom' ? y - flyoutHeight - TOOLTIP_POSITION.FLYOUT_GAP : y + TOOLTIP_POSITION.FLYOUT_GAP;
  let flyoutX = x - flyoutWidth / 2;

  // Constrain horizontal position to stay within chart boundaries
  if (flyoutX < margin) {
    flyoutX = margin;
  } else if (flyoutX + flyoutWidth > chartWidth - margin) {
    flyoutX = chartWidth - flyoutWidth - margin;
  }

  // Constrain vertical position to stay within chart boundaries
  if (flyoutY < margin) {
    flyoutY = margin;
  } else if (flyoutY + flyoutHeight > chartHeight - margin) {
    flyoutY = chartHeight - flyoutHeight - margin;
  }

  return { flyoutX, flyoutY };
};

/**
 * Calculates the pointer (triangle) position for a tooltip.
 *
 * The pointer connects the flyout box to the data point, appearing either
 * at the top or bottom of the flyout depending on orientation.
 *
 * @param config - Configuration object containing pointer parameters
 * @returns The calculated pointer position and direction
 *
 * @example
 * const pointer = calculateTooltipPointerPosition({
 *   x: 200,
 *   flyoutY: 40,
 *   flyoutHeight: 100,
 *   orientation: 'bottom',
 * });
 * // Returns: { pointerLength: 4, pointerY: 140, pointerDirection: 1 }
 */
export const calculateTooltipPointerPosition = (config: TooltipPointerConfig): TooltipPointerPosition => {
  const { flyoutY, flyoutHeight, orientation } = config;

  const pointerLength = TOOLTIP_POSITION.POINTER_LENGTH;
  // When orientation is 'bottom', pointer is at the bottom of the flyout
  // When orientation is 'top', pointer is at the top of the flyout
  const pointerY = orientation === 'bottom' ? flyoutY + flyoutHeight : flyoutY;
  // Direction determines which way the triangle points
  // 1 = points down, -1 = points up
  const pointerDirection = orientation === 'bottom' ? 1 : -1;

  return { pointerLength, pointerY, pointerDirection };
};
